import {
    all,
    call,
    put,
    select,
} from 'redux-saga/effects';
import { AvcAction } from './enum';
import {
    selectAddressForm,
    selectSelectedAddress,
} from './selector';
import { AddressForm } from './interface';
import { checkAddress } from '../../util/jarvis';
import {
    Address,
    getAddressCookie,
    getHandshake,
    getZpApiHandshake,
    takeLatestWithCancel,
} from '@vfde-sails/avc-core';
import {
    selectAvailabilityStatus,
    selectIsLoggedInCableCustomer,
    selectSelectedTariffId,
} from '../App/selector';
import {
    JarvisAlternativeResponse,
    JarvisAvcResponse,
} from '../../interface';
import {
    isExistingCableProductResponse,
    isSuccessResponse,
} from '../../util/typeGuard';
import {
    setError,
    setSelectedAddress,
    Actions,
    checkSeamlessAvailability,
} from './action';
import { ErrorType } from '../App/enum';
import {
    resetState,
    setCustomer,
    startLoading,
    stopLoading,
} from '../App/action';
import {
    startLoadingOverlay,
    stopLoadingOverlay,
} from '../Overlay/action';
import { debugInfo } from '../../util/debug';
import { trackError } from '../../tracking/tracking';
import { TrackingFormStepName } from '../../tracking/enum';
import {
    trackAvcFormSaga,
    handleSuccessResponse,
    checkAlternativeAvailability,
    validateAddress,
} from './util/generator';
import { OverlayAction } from '../Overlay/enum';
import {
    setPageUrl,
    trackPageViewSaga,
} from '../App/util/generator';

/**
 * Checks selected tariffs availability on the user's entered address
 * or inits check without address for logged in customers
 */
export function* checkAddressSaga (action: Actions, isSeamless = false) {
    yield call(trackAvcFormSaga, TrackingFormStepName.START, isSeamless);

    const selectedTariffId: string = yield select(selectSelectedTariffId);
    const addressForm: AddressForm = yield select(selectAddressForm);
    const isLoggedInCableCustomer: boolean = yield select(selectIsLoggedInCableCustomer);
    const selectedAddress: Address | undefined = yield select(selectSelectedAddress);

    try {
        yield put(startLoading(action.type));
        yield put(startLoadingOverlay(action.type));
        yield put(setError());

        yield put(resetState());

        const gimliState: string | undefined = yield call(getHandshake);
        const zpApiState: string | undefined = yield call(getZpApiHandshake);

        const address: Address | undefined = yield call(validateAddress, addressForm, selectedAddress, isLoggedInCableCustomer, gimliState);

        if (!address) {
            return;
        }

        const [alternativeResult, avcResponse]: [JarvisAlternativeResponse, JarvisAvcResponse] = yield all([
            call(checkAlternativeAvailability, action, address, zpApiState, isSeamless),
            call(checkAddress, selectedTariffId, isLoggedInCableCustomer, address, gimliState),
        ]);

        if (isExistingCableProductResponse(avcResponse)) {
            yield put(setCustomer(avcResponse.customerInfo, avcResponse.address));
            yield call(trackPageViewSaga);

            return;
        }

        if (isSuccessResponse(avcResponse)) {
            yield call(handleSuccessResponse, action, avcResponse, alternativeResult, isSeamless);

            return;
        }

        throw Error('Unexpected response');
    }
    catch (error) {
        debugInfo(error);
        yield put(setError({ error: error as Error, type: ErrorType.AVC_ERROR }));
        yield call(trackError, error as Error, ErrorType.AVC_ERROR);
    }
    finally {
        yield call(setPageUrl);
        yield put(stopLoadingOverlay(action.type));
        yield put(stopLoading(action.type));
    }
}

/**
 * Inits address check if availibility is not in session storage and address present in cookie
 */
export function* checkSeamlessAvailabilitySaga (action: ReturnType<typeof checkSeamlessAvailability>) {
    const isLoggedInCableCustomer: boolean = yield select(selectIsLoggedInCableCustomer);

    if (isLoggedInCableCustomer) {
        yield call(checkAddressSaga, action, true);

        return;
    }

    const isAvailbilityChecked: boolean = yield select(selectAvailabilityStatus);
    const address: Address | undefined = yield call(getAddressCookie);

    yield put(setSelectedAddress(0, address));

    if ((isAvailbilityChecked && !action.forceSeamless) || !address) {
        return;
    }

    yield call(checkAddressSaga, action, true);
}

/**
 * AVC Container saga
 */
export default function* saga () {
    yield takeLatestWithCancel(
        AvcAction.CHECK_ADDRESS,
        checkAddressSaga,
        OverlayAction.CANCEL_AVAILABILITY_CHECK,
        OverlayAction.PREVENT_CANCEL,
    );
    yield takeLatestWithCancel(
        AvcAction.CHECK_SEAMLESS_AVAILABILITY,
        checkSeamlessAvailabilitySaga,
        OverlayAction.CANCEL_AVAILABILITY_CHECK,
        OverlayAction.PREVENT_CANCEL,
    );
}
